import type { SelectableItem } from "@/components/screener/DualPanelItemSelector";
import { isExposurePrimaryColumn } from "./exposurePrimaryColumns";

export const DEFAULT_CONTRIBUTION_COLUMNS = ["name", "avg_weight", "contribution"];

/** Truncate security names once this many data columns are visible. */
export const CONTRIBUTION_NAME_TRUNCATE_COLUMN_THRESHOLD = 5;

export function shouldTruncateContributionSecurityName(
  visibleColumnCount: number
): boolean {
  return visibleColumnCount >= CONTRIBUTION_NAME_TRUNCATE_COLUMN_THRESHOLD;
}

/** localStorage key for the per-user contribution column selection. */
export const CONTRIBUTION_COLUMNS_STORAGE_KEY = "contributions-selected-columns";

export interface ContributionColumnDef extends SelectableItem {
  sortKey: string;
  sortable?: boolean;
  effectiveSortKey?: string;
  isPerformance?: boolean;
  isExposurePrimary?: boolean;
  requiresWeightColumns?: boolean;
  requiresCurrentWeight?: boolean;
}

export const SNAPSHOT_TEXT_COLUMN_IDS = new Set([
  "base_currency",
  "short_name",
  "returns_as_of",
]);

export const CONTRIBUTION_COLUMNS: ContributionColumnDef[] = [
  {
    id: "name",
    name: "Security",
    group: "Contributions",
    sortKey: "name",
    sortable: true,
  },
  {
    id: "base_currency",
    name: "Base Currency",
    group: "Fundamentals",
    sortKey: "base_currency",
    sortable: true,
  },
  {
    id: "short_name",
    name: "Short Name",
    group: "Fundamentals",
    sortKey: "short_name",
    sortable: true,
  },
  {
    id: "returns_as_of",
    name: "Data as of",
    group: "Fundamentals",
    sortKey: "returns_as_of",
    sortable: true,
  },
  {
    id: "avg_weight",
    name: "Avg Weight (%)",
    group: "Weights",
    sortKey: "avg_weight",
    effectiveSortKey: "effective_avg_weight",
  },
  {
    id: "start_weight",
    name: "Start Wgt (%)",
    group: "Weights",
    sortKey: "start_weight",
    requiresWeightColumns: true,
  },
  {
    id: "end_weight",
    name: "End Wgt (%)",
    group: "Weights",
    sortKey: "end_weight",
    requiresWeightColumns: true,
  },
  {
    id: "start_value_portfolio_ccy",
    name: "Start Value",
    group: "Weights",
    sortKey: "start_value_portfolio_ccy",
    requiresWeightColumns: true,
  },
  {
    id: "end_value_portfolio_ccy",
    name: "End Value",
    group: "Weights",
    sortKey: "end_value_portfolio_ccy",
    requiresWeightColumns: true,
  },
  {
    id: "weight",
    name: "Curr Wgt (%)",
    group: "Weights",
    sortKey: "weight",
    requiresCurrentWeight: true,
  },
  {
    id: "contribution",
    name: "Contrib (%)",
    group: "Contribution",
    sortKey: "contribution",
    effectiveSortKey: "effective_contribution",
  },
  {
    id: "cumulative_local_return",
    name: "Local (%)",
    group: "Performance",
    sortKey: "cumulative_local_return",
    isPerformance: true,
  },
  {
    id: "cumulative_fx_return",
    name: "FX (%)",
    group: "Performance",
    sortKey: "cumulative_fx_return",
    isPerformance: true,
  },
  {
    id: "cumulative_portfolio_return",
    name: "Total (%)",
    group: "Performance",
    sortKey: "cumulative_portfolio_return",
    isPerformance: true,
  },
  {
    id: "cumulative_benchmark_return",
    name: "Bench (%)",
    group: "Performance",
    sortKey: "cumulative_benchmark_return",
    isPerformance: true,
  },
  {
    id: "cumulative_relative_return",
    name: "Relative (%)",
    group: "Performance",
    sortKey: "cumulative_relative_return",
    isPerformance: true,
  },
  {
    id: "relative_contribution",
    name: "Rel. Contrib (%)",
    group: "Performance",
    sortKey: "relative_contribution",
    effectiveSortKey: "effective_relative_contribution",
    isPerformance: true,
  },
];

const COLUMN_MAP = new Map(
  CONTRIBUTION_COLUMNS.map((column) => [column.id, column])
);

export function getContributionColumnDef(
  columnId: string
): ContributionColumnDef | undefined {
  return COLUMN_MAP.get(columnId);
}

export function isPerformanceColumn(columnId: string): boolean {
  return COLUMN_MAP.get(columnId)?.isPerformance ?? false;
}

export function getContributionSortKey(
  columnId: string,
  enableLookthrough: boolean
): string {
  if (columnId === "name" || isSnapshotTextColumn(columnId)) {
    return columnId;
  }
  const column = COLUMN_MAP.get(columnId);
  if (!column) return columnId;
  if (enableLookthrough && column.effectiveSortKey) {
    return column.effectiveSortKey;
  }
  return column.sortKey;
}

export function compareContributionSortValues(
  a: ContributionRowData,
  b: ContributionRowData,
  columnId: string,
  direction: "asc" | "desc",
  enableLookthrough: boolean,
  aggregateMode: boolean
): number {
  if (columnId === "name") {
    const aName = String(a.name ?? "");
    const bName = String(b.name ?? "");
    const cmp = aName.localeCompare(bName, undefined, { sensitivity: "base" });
    return direction === "asc" ? cmp : -cmp;
  }

  if (columnId === "returns_as_of") {
    const rawA = a.returns_as_of;
    const rawB = b.returns_as_of;
    const emptyA = rawA === undefined || rawA === null || rawA === "";
    const emptyB = rawB === undefined || rawB === null || rawB === "";
    if (emptyA && emptyB) return 0;
    if (emptyA) return 1;
    if (emptyB) return -1;
    const cmp = String(rawA).localeCompare(String(rawB));
    return direction === "asc" ? cmp : -cmp;
  }

  const textA = getContributionCellText(a, columnId);
  const textB = getContributionCellText(b, columnId);
  if (textA !== undefined || textB !== undefined) {
    const emptyA = textA === undefined;
    const emptyB = textB === undefined;
    if (emptyA && emptyB) return 0;
    if (emptyA) return 1;
    if (emptyB) return -1;
    const cmp = textA.localeCompare(textB, undefined, { sensitivity: "base" });
    return direction === "asc" ? cmp : -cmp;
  }

  const aVal = getContributionCellNumber(
    a,
    columnId,
    enableLookthrough,
    aggregateMode
  );
  const bVal = getContributionCellNumber(
    b,
    columnId,
    enableLookthrough,
    aggregateMode
  );
  const aNum = aVal ?? 0;
  const bNum = bVal ?? 0;
  return direction === "asc" ? aNum - bNum : bNum - aNum;
}

export function getContributionColumnLabel(
  columnId: string,
  enableLookthrough: boolean,
  portfolioCurrency?: string
): string {
  const column = COLUMN_MAP.get(columnId);
  if (!column) return columnId;

  if (columnId === "avg_weight" && enableLookthrough) {
    return "Eff. Avg Wgt (%)";
  }
  if (columnId === "contribution" && enableLookthrough) {
    return "Eff. Contrib (%)";
  }
  if (columnId === "relative_contribution" && enableLookthrough) {
    return "Eff. Rel. Contrib (%)";
  }
  if (columnId === "start_value_portfolio_ccy" && portfolioCurrency) {
    return `Start Value (${portfolioCurrency})`;
  }
  if (columnId === "end_value_portfolio_ccy" && portfolioCurrency) {
    return `End Value (${portfolioCurrency})`;
  }

  if (isExposurePrimaryColumn(columnId)) {
    return columnId
      .replace(/_primary$/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return column.name;
}

export function filterVisibleContributionColumns(
  selectedColumns: string[],
  options: {
    hasWeightColumns: boolean;
    hasCurrentWeight: boolean;
  }
): string[] {
  return selectedColumns.filter((columnId) => {
    if (isSnapshotTextColumn(columnId)) {
      return true;
    }
    const column = COLUMN_MAP.get(columnId);
    if (!column) return false;
    if (column.requiresWeightColumns && !options.hasWeightColumns) {
      return false;
    }
    if (column.requiresCurrentWeight && !options.hasCurrentWeight) {
      return false;
    }
    return true;
  });
}

export function loadContributionColumns(
  availableColumnIds?: string[]
): string[] {
  if (typeof window === "undefined") return DEFAULT_CONTRIBUTION_COLUMNS;
  try {
    const raw = window.localStorage.getItem(CONTRIBUTION_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_CONTRIBUTION_COLUMNS;
    const parsed = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every((id) => typeof id === "string") &&
      parsed.length > 0
    ) {
      const allowedIds = availableColumnIds
        ? new Set(availableColumnIds)
        : null;
      const valid = parsed.filter(
        (id) =>
          COLUMN_MAP.has(id) ||
          isSnapshotTextColumn(id) ||
          (allowedIds?.has(id) ?? false)
      );
      const selected =
        valid.length > 0 ? valid : DEFAULT_CONTRIBUTION_COLUMNS;
      return ensureSecurityColumnSelected(selected);
    }
    return DEFAULT_CONTRIBUTION_COLUMNS;
  } catch {
    return DEFAULT_CONTRIBUTION_COLUMNS;
  }
}

function ensureSecurityColumnSelected(columns: string[]): string[] {
  if (columns.includes("name")) {
    return columns;
  }
  return ["name", ...columns];
}

export function saveContributionColumns(columns: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CONTRIBUTION_COLUMNS_STORAGE_KEY,
      JSON.stringify(ensureSecurityColumnSelected(columns))
    );
  } catch {
    // Ignore storage failures (e.g. private mode / quota).
  }
}

export interface ContributionRowData {
  [key: string]: string | number | boolean | undefined;
  avg_weight?: number;
  effective_avg_weight?: number;
  start_weight?: number;
  end_weight?: number;
  start_value_portfolio_ccy?: number;
  end_value_portfolio_ccy?: number;
  weight?: number;
  contribution?: number;
  effective_contribution?: number;
  cumulative_local_return?: number;
  cumulative_fx_return?: number;
  cumulative_portfolio_return?: number;
  cumulative_benchmark_return?: number;
  cumulative_relative_return?: number;
  relative_contribution?: number;
  effective_relative_contribution?: number;
}

export function isSnapshotTextColumn(columnId: string): boolean {
  return (
    isExposurePrimaryColumn(columnId) || SNAPSHOT_TEXT_COLUMN_IDS.has(columnId)
  );
}

/** Columns rendered left-aligned like the Security column. */
export function isLeftAlignedContributionColumn(columnId: string): boolean {
  return (
    columnId === "name" ||
    columnId === "short_name" ||
    columnId === "returns_as_of"
  );
}

export function extractSnapshotTextColumns(columnIds: string[]): string[] {
  return columnIds.filter((columnId) => SNAPSHOT_TEXT_COLUMN_IDS.has(columnId));
}

function formatContributionDateValue(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function getContributionCellText(
  item: ContributionRowData,
  columnId: string
): string | undefined {
  if (!isSnapshotTextColumn(columnId)) return undefined;
  const value = item[columnId];
  if (value === undefined || value === null || value === "") return undefined;
  if (columnId === "returns_as_of") {
    return formatContributionDateValue(String(value));
  }
  return String(value);
}

export function getContributionCellNumber(
  item: ContributionRowData,
  columnId: string,
  enableLookthrough: boolean,
  aggregateMode: boolean
): number | undefined {
  if (isSnapshotTextColumn(columnId)) return undefined;

  const useEffective = enableLookthrough || aggregateMode;

  switch (columnId) {
    case "avg_weight":
      return useEffective && typeof item.effective_avg_weight === "number"
        ? item.effective_avg_weight
        : item.avg_weight;
    case "start_weight":
      return item.start_weight;
    case "end_weight":
      return item.end_weight;
    case "start_value_portfolio_ccy":
      return item.start_value_portfolio_ccy;
    case "end_value_portfolio_ccy":
      return item.end_value_portfolio_ccy;
    case "weight":
      return item.weight;
    case "contribution":
      return useEffective && typeof item.effective_contribution === "number"
        ? item.effective_contribution
        : item.contribution;
    case "cumulative_local_return":
      return item.cumulative_local_return;
    case "cumulative_fx_return":
      return item.cumulative_fx_return;
    case "cumulative_portfolio_return":
      return item.cumulative_portfolio_return;
    case "cumulative_benchmark_return":
      return item.cumulative_benchmark_return;
    case "cumulative_relative_return":
      return item.cumulative_relative_return;
    case "relative_contribution":
      return useEffective &&
        typeof item.effective_relative_contribution === "number"
        ? item.effective_relative_contribution
        : item.relative_contribution;
    default:
      return undefined;
  }
}

export function isPercentageContributionColumn(columnId: string): boolean {
  return (
    !isSnapshotTextColumn(columnId) &&
    columnId !== "start_value_portfolio_ccy" &&
    columnId !== "end_value_portfolio_ccy"
  );
}

export function shouldShowContributionFooterDash(
  columnId: string
): boolean {
  if (isSnapshotTextColumn(columnId)) return false;
  return [
    "cumulative_local_return",
    "cumulative_fx_return",
    "cumulative_portfolio_return",
    "cumulative_benchmark_return",
    "cumulative_relative_return",
  ].includes(columnId);
}
